/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import {
  CAPACITY_DATA_LOAD,
  CAPACITY_DATA_LOAD_SUCCESS,
  DEMOGRAPHICS_FILTER,
  DEMOGRAPHICS_FILTER_COUNTRY_VALUE_REMOVE,
  DEMOGRAPHICS_FILTER_REGION_VALUE_REMOVE,
  DEMOGRAPHICS_FILTER_RESET,
  DEMOGRAPHICS_FILTER_SUCCESS,
  DEMOGRAPHICS_FILTER_VALUE_ADD,
  DEMOGRAPHICS_FILTER_VALUE_REMOVE,
  DEMOGRAPHICS_FILTER_VALUE_UPDATE,
} from "Constants/ActionTypes";
import { defaultMvpDemographicsFilterValues } from "Constants/ApplicationConstants";
import { TenantTypesEnum } from "Constants/TenantTypes";

import {
  DemographicActionTypes,
  ICapDataLoad,
  IDemographicsFilterValueAdd,
  IDemographicsFilterValueRemove,
  IDemographicsFilterValueUpdate,
  IFilterDemographics,
} from "./DemographicsAction";
import { IDemographicsState } from "./IDemographicsState";

const INITIAL_STATE: IDemographicsState = {
  demographicsData: {
    numberOfApplications: 0,
    averageApplicationScore: 0,
    genderData: [],
    statusData: [],
    declineReasonData: [],
    awardCategoryBarData: [],
    regionBarData: [],
  },
  capData: {
    capacityData: [],
  },
  demographicsFilterValues: defaultMvpDemographicsFilterValues,
  isLoadingDemographics: false,
  isLoadingCapData: false,
  loadDemographicsError: "",
  loadCapDataError: "",
  tenant: TenantTypesEnum.MVP,
};

const applyFilterDemographics = (
  state: IDemographicsState,
  action: IFilterDemographics
): IDemographicsState => ({
  ...state,
  demographicsData: INITIAL_STATE.demographicsData,
  isLoadingDemographics: true,
  loadDemographicsError: "",
  tenant: action.tenant,
});

const applyFilterDemographicsSuccess = (state: IDemographicsState, action): IDemographicsState => ({
  ...state,
  demographicsData: action.demographics,
  isLoadingDemographics: false,
  loadDemographicsError: "",
});

const applyFilterDemographicsFail = (state: IDemographicsState, action): IDemographicsState => ({
  ...state,
  demographicsData: INITIAL_STATE.demographicsData,
  isLoadingDemographics: false,
  loadDemographicsError: "Couldn't load demographics data: " + action.errorMessage,
});

const addDemographicsFilterValue = (
  state: IDemographicsState,
  action: IDemographicsFilterValueAdd
): IDemographicsState => ({
  ...state,
  demographicsFilterValues: state.demographicsFilterValues.concat([action.filterValue]),
});

const applyDemographicsFilterReset = (state: IDemographicsState): IDemographicsState => ({
  ...state,
  demographicsFilterValues: defaultMvpDemographicsFilterValues,
});

const removeDemographicsFilterValue = (
  state: IDemographicsState,
  action: IDemographicsFilterValueRemove
): IDemographicsState => ({
  ...state,
  demographicsFilterValues: state.demographicsFilterValues.filter(
    (item) =>
      !(item.fieldName === action.filterValue.fieldName && item.value === action.filterValue.value)
  ),
});

const removeDemographicsFilterRegionValue = (state: IDemographicsState): IDemographicsState => ({
  ...state,
  demographicsFilterValues: state.demographicsFilterValues.filter(
    (item) => !(item.fieldName === "region")
  ),
});

const removeDemographicsFilterCountryValue = (state: IDemographicsState): IDemographicsState => ({
  ...state,
  demographicsFilterValues: state.demographicsFilterValues.filter(
    (item) => !(item.fieldName === "country")
  ),
});

const updateDemographicsFilterValue = (
  state: IDemographicsState,
  action: IDemographicsFilterValueUpdate
): IDemographicsState => ({
  ...state,
  demographicsFilterValues: state.demographicsFilterValues
    .filter((item) => !(item.fieldName === action.filterValue.fieldName))
    .concat([action.filterValue]),
});

const applyCapacityDataLoad = (state: IDemographicsState, _: ICapDataLoad): IDemographicsState => ({
  ...state,
  capData: INITIAL_STATE.capData,
  isLoadingCapData: true,
  loadCapDataError: "",
  tenant: TenantTypesEnum.MVP,
});

const applyCapacityDataLoadSuccess = (state: IDemographicsState, action): IDemographicsState => ({
  ...state,
  capData: action.capData,
  isLoadingCapData: false,
  loadCapDataError: "",
});

const applyCapacityDataLoadFail = (state: IDemographicsState, action): IDemographicsState => ({
  ...state,
  capData: INITIAL_STATE.capData,
  isLoadingCapData: false,
  loadCapDataError: "Couldn't load capacity data: " + action.errorMessage,
});

function applicationReducer(
  state = INITIAL_STATE,
  action: DemographicActionTypes
): IDemographicsState {
  switch (action.type) {
    case DEMOGRAPHICS_FILTER: {
      return applyFilterDemographics(state, action);
    }
    case DEMOGRAPHICS_FILTER_SUCCESS: {
      return applyFilterDemographicsSuccess(state, action);
    }
    case DEMOGRAPHICS_FILTER_RESET: {
      return applyDemographicsFilterReset(state);
    }
    case DEMOGRAPHICS_FILTER_VALUE_ADD: {
      return addDemographicsFilterValue(state, action);
    }
    case DEMOGRAPHICS_FILTER_VALUE_REMOVE: {
      return removeDemographicsFilterValue(state, action);
    }
    case DEMOGRAPHICS_FILTER_COUNTRY_VALUE_REMOVE: {
      return removeDemographicsFilterCountryValue(state);
    }
    case DEMOGRAPHICS_FILTER_REGION_VALUE_REMOVE: {
      return removeDemographicsFilterRegionValue(state);
    }
    case DEMOGRAPHICS_FILTER_VALUE_UPDATE: {
      return updateDemographicsFilterValue(state, action);
    }
    case CAPACITY_DATA_LOAD: {
      return applyCapacityDataLoad(state, action);
    }
    case CAPACITY_DATA_LOAD_SUCCESS: {
      return applyCapacityDataLoadSuccess(state, action);
    }
    default:
      return state;
  }
}

export default applicationReducer;
